// PATH: src/components/IDE/IDEPhaserAdmin.tsx
import React, { useEffect, useRef, useState } from 'react';
import PhaserView, { PhaserRef } from '../Phaser';
import { EventManager } from '../Phaser/engine/events/EventManager';
import { useObjectsApi } from '../../api/objects/objects';
import { TSprite } from '../../models/objects/sprite';
import { SaveOutlined } from '@ant-design/icons';
import { Col, message, Row, Button, Modal } from 'antd';
import { TObstacle, TScene } from '../../models/objects/objects';
import { IDEPhaserConfig } from './config';
import { TLesson } from '../../models/courses/lesson';
import { useCourseApi } from '../../api/courses/courses';
import { getDefaultConfigPhaser } from '../Phaser/types/config';
import { TSound } from '../../models/objects/media';
import JsonEditor from '../JsonEditor/JsonEditor';

// NEW IDE
import { UnifiedIDE } from '../IDE/ide'; // ou '@/components/IDE/ide' selon ton alias
import { getIDEConfigDefault, LanguageId } from '../IDE/ide/core/types';
import { mapImportedPhaserConfig } from '../../utils/mapImportedPhaserConfig';
import stripPhaserAutoFields from '../../utils/stripPhaserAutoFields';
import IDEPhaserTeacher from './IDEPhaserTeacher';

interface IDEPhaserAdminProps {
    language: LanguageId;
    editingLesson: TLesson | undefined;
    teacherMode?: boolean;
}

const IDEPhaserAdmin: React.FC<IDEPhaserAdminProps> = (props) => {
    const phaserRef = useRef<PhaserRef>(null);
    const eventManagerRef = useRef<EventManager | null>(null);

    const { getSpritesDetails, getScenes, getObstacles, getSounds } = useObjectsApi();
    const { saveLessonDetails } = useCourseApi();

    const [config, setConfig] = useState<IDEPhaserConfig | undefined>();
    const [dbScenes, setDbScenes] = useState<TScene[]>([]);
    const [sprites, setSprites] = useState<TSprite[]>([]);
    const [dbObstacles, setDbObstacles] = useState<TObstacle[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [sounds, setSounds] = useState<TSound[]>([]);
    // 👉 on garde le dernier code saisi par l'IDE pour le persist
    const [latestCode, setLatestCode] = useState<string>('');
    // 👉 on garde le dernier code enseignant saisi par l'IDE pour le persist
    const [latestTeacherCode, setLatestTeacherCode] = useState<string>('');
    const [importOpen, setImportOpen] = useState<boolean>(false);
    const [importedStatement, setImportedStatement] = useState<string>('');
    useEffect(() => {
        if (!config) return;
        setConfig((prev) =>
            prev
                ? {
                      configMirror: {
                          ...getIDEConfigDefault(),
                          ...(prev.configMirror ?? {}),
                      },
                      configPhaser: mapImportedPhaserConfig(
                          prev.configPhaser,
                          sprites,
                          dbObstacles,
                          dbScenes,
                      ),
                  }
                : prev,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sprites, dbObstacles, dbScenes]);

    useEffect(() => {
        if (!props.editingLesson || !props.editingLesson.config || !props.editingLesson.config.config) {
            const init = {
                configMirror: getIDEConfigDefault(),
                configPhaser: getDefaultConfigPhaser(),
            };
            setConfig(init as IDEPhaserConfig);
            // initialise latestCode avec le code par défaut
            setLatestCode(init.configMirror.initialCode || '');
            setLatestTeacherCode(init.configMirror.teacherCode || '');
            setImportedStatement('');
        } else {
            const cfg = props.editingLesson.config.config as IDEPhaserConfig;
            setConfig(cfg);
            setLatestCode(cfg.configMirror?.initialCode || '');
            setLatestTeacherCode(cfg.configMirror?.teacherCode || '');
            // Initialize imported statement from lesson
            setImportedStatement(props.editingLesson.statement || '');
        }

        const eventManager = new EventManager();
        eventManagerRef.current = eventManager;
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        setLoading(true);
        Promise.all([getSpritesDetails(), getScenes(), getObstacles(), getSounds()])
            .then((results) => {
                const [charactersData, scenesData, obstaclesData, soundsData] = results;
                setSprites(charactersData.data);
                setDbScenes(scenesData.data);
                setDbObstacles(obstaclesData.data);
                setSounds(soundsData.data);
            })
            .catch((err) => {
                message.error(err.response?.data ?? err.message ?? err);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line
    }, []);

    const save = async (): Promise<void> => {
        if (!props.editingLesson || !config) return;

        // 🔐 on reconstruit la config IDE en gardant tout l'existant mais avec le code courant
        let configurationMirror;
        let configurationPhaser;

        if (props.teacherMode) {
            // Mode enseignant: on ne sauvegarde que teacherCode, on préserve initialCode
            configurationMirror = {
                ...config.configMirror,
                teacherCode: latestTeacherCode, // <- code saisi dans la vue enseignant
            };
            // En mode enseignant, on ne récupère pas la config Phaser (pas de phaserRef)
            configurationPhaser = config.configPhaser;
        } else {
            // Mode admin: on sauvegarde initialCode et teacherCode
            if (!phaserRef.current) return;
            configurationMirror = {
                ...config.configMirror,
                initialCode: latestCode, // <- code saisi dans le nouvel IDE (admin)
                teacherCode: latestTeacherCode, // <- préserver le code enseignant
            };
            configurationPhaser = phaserRef.current.getConfig();
        }

        try {
            const data: TLesson = {
                ...props.editingLesson,
                // Prioritize imported statement over existing lesson statement
                statement: importedStatement || props.editingLesson.statement || '',
                config: {
                    id: 0,
                    lesson_id: props.editingLesson.id,
                    config: {
                        configMirror: configurationMirror,
                        configPhaser: configurationPhaser,
                    } as IDEPhaserConfig,
                },
            };
            await saveLessonDetails(data);
            message.success('Leçon sauvegardée.');
        } catch (err: any) {
            message.error(err.response?.data ?? err.message ?? err);
        }
    };

    // Mode enseignant: afficher IDEPhaserTeacher (comme vue étudiant)
    if (props.teacherMode) {
        return (
            <div style={{ width: '100%', height: '100%' }}>
                {config && !loading && (
                    <IDEPhaserTeacher
                        lesson={props.editingLesson}
                        language={props.language}
                        onCodeChange={(code: string) => setLatestTeacherCode(code)}
                    />
                )}
                <Button 
                    type="default" 
                    shape="circle"
                    icon={<SaveOutlined />} 
                    onClick={save}
                    style={{
                        position: 'fixed',
                        bottom: '24px',
                        right: '24px',
                        width: '56px',
                        height: '56px',
                        zIndex: 1000,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        color: '#1890ff'
                    }}
                />
            </div>
        );
    }

    // Mode admin: afficher l'interface admin normale
    return (
        <div style={{ width: '100%', height: '100%' }}>
            {config && (
                <div style={{ marginBottom: 16 }}>
                    <Button onClick={() => setImportOpen(true)}>Importer config</Button>
                </div>
            )}            <Row gutter={16} style={{ height: 'calc(100vh - 100px)' }}>
                {config && !loading && (
                    <>
                        <Col span={16}>
                            <UnifiedIDE
                                language={props.language}
                                initialCode={latestCode || config.configMirror.initialCode || ''}
                                projectMode={false}
                                adminMode={true}

                                sprites={sprites}
                                scenesConfig={dbScenes}
                                obstacles={dbObstacles}
                                sounds={sounds}

                                // on stop → reset la scène Phaser comme avant
                                onStop={async () => {
                                    await phaserRef.current?.handleResetConfig();
                                }}

                                // quand on clique "Rétablir" dans la toolbar de l'IDE
                                onRestoreInitial={() => {
                                    setLatestCode(config.configMirror.initialCode || '');
                                }}

                                // récupère le code en direct pour le sauvegarder
                                onCodeChange={(code: string) => setLatestCode(code)}

                                helpStorageKey={props.editingLesson ? `lesson-${props.editingLesson.id}` : null}
                                helpConfig={config.configMirror?.helpConfig}
                                helpGrouped={config.configMirror?.helpGrouped}
                                onPersistHelpConfig={(_, cfg, grouped) => {
                                    setConfig((prev) =>
                                        prev
                                            ? {
                                                  ...prev,
                                                  configMirror: {
                                                      ...(prev.configMirror ?? getIDEConfigDefault()),
                                                      helpConfig: cfg,
                                                      helpGrouped: grouped,
                                                  },
                                              }
                                            : prev,
                                    );
                                }}

                            />
                        </Col>
                        <Col span={8}>
                            <PhaserView
                                projectMode={true}
                                adminMode={true}
                                ref={phaserRef}
                                eventManager={eventManagerRef.current}
                                config={config.configPhaser}
                                isRunning={true}
                                onWin={() => console.log('win')}
                                sprites={sprites}
                                scenesConfig={dbScenes}
                                obstacles={dbObstacles}
                                textsConfig={[]}
                                sounds={sounds}
                            />
                        </Col>
                    </>
                )}
            </Row>
            <Modal
                title="Importer une configuration IDEPhaser"
                open={importOpen}
                onCancel={() => setImportOpen(false)}
                footer={null}
                width={800}
                destroyOnClose
            >
                <JsonEditor<IDEPhaserConfig>
                    title="IDEPhaser Config"
                    value={
                        config
                            ? {
                                  ...config,
                                  // configMirror includes initialCode and teacherCode
                                  configPhaser: stripPhaserAutoFields(
                                      config.configPhaser,
                                  ),
                              }
                            : config
                    }
                    showStatement={true}
                    initialStatement={importedStatement}
                    onStatementChange={setImportedStatement}
                    onApply={(data) => {
                        // Merge imported data with defaults (includes teacherCode support)
                        const mapped: IDEPhaserConfig = {
                            configMirror: {
                                ...getIDEConfigDefault(), // Provides teacherCode: '' default
                                ...(data.configMirror ?? {}), // Overrides with imported data (may include teacherCode)
                            },
                            configPhaser: mapImportedPhaserConfig(
                                data.configPhaser,
                                sprites,
                                dbObstacles,
                                dbScenes,
                            ),
                        };
                        setConfig(mapped);
                        setLatestCode(mapped.configMirror.initialCode || '');
                        // Initialize teacherCode from imported config (backward compatible: defaults to '' if not present)
                        setLatestTeacherCode(mapped.configMirror.teacherCode || '');
                        setImportOpen(false);
                        message.success('Configuration importée');
                    }}
                />
            </Modal>

            <Button 
                type="default" 
                shape="circle"
                icon={<SaveOutlined />} 
                onClick={save}
                style={{
                    position: 'fixed',
                    bottom: '24px',
                    right: '24px',
                    width: '56px',
                    height: '56px',
                    zIndex: 1000,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    color: '#1890ff'
                }}
            />
        </div>
    );
};

export default IDEPhaserAdmin;
